import { Box, Button, Card, Flex, Image, Stack, Text, ThemeIcon } from '@mantine/core';
import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { IconPhoto, IconUpload, IconX } from '@tabler/icons-react';
import { useMemo } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

const MAX_LOGO_SIZE = 5 * 1024 * 1024;

export default function CompanyLogoCard({
  file,
  onChange,
}: {
  file: File | null;
  onChange: (file: File | null) => void;
}) {
  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);

  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="sm">
        <Stack gap={2}>
          <Text fw={FONT_WEIGHTS.semibold}>Company logo</Text>
          <Text size="textSm" c="gray.6">
            Upload once, then tick the &quot;Use company logo&quot; checkbox on any slot below to
            reuse it.
          </Text>
        </Stack>

        {file && previewUrl ? (
          <Flex align="center" gap="md" p="sm" bg="gray.0" style={{ borderRadius: 8 }}>
            <Box
              w={96}
              h={96}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Image src={previewUrl} alt="Company logo" fit="contain" h={96} />
            </Box>
            <Stack gap={2} flex={1}>
              <Text size="textSm" fw={FONT_WEIGHTS.medium}>
                {file.name}
              </Text>
              <Text size="textSm" c="gray.6">
                {(file.size / 1024).toFixed(0)} KB
              </Text>
            </Stack>
            <Button variant="subtle" color="red" size="xs" onClick={() => onChange(null)}>
              Remove
            </Button>
          </Flex>
        ) : (
          <Dropzone
            onDrop={files => {
              const next = files[0];
              if (next) onChange(next);
            }}
            onReject={() =>
              notifications.show({
                title: 'Rejected',
                message: 'Logo must be an image under 5MB.',
                color: 'red',
              })
            }
            maxSize={MAX_LOGO_SIZE}
            maxFiles={1}
            accept={IMAGE_MIME_TYPE}
            style={{
              border: '1px dashed var(--mantine-color-gray-4)',
              borderRadius: 8,
              padding: 32,
            }}
          >
            <Flex direction="column" align="center" gap="xs">
              <Dropzone.Accept>
                <ThemeIcon variant="light" color="blue" size={48} radius="xl">
                  <IconUpload size={24} />
                </ThemeIcon>
              </Dropzone.Accept>
              <Dropzone.Reject>
                <ThemeIcon variant="light" color="red" size={48} radius="xl">
                  <IconX size={24} />
                </ThemeIcon>
              </Dropzone.Reject>
              <Dropzone.Idle>
                <ThemeIcon variant="light" color="gray" size={48} radius="xl">
                  <IconPhoto size={24} />
                </ThemeIcon>
              </Dropzone.Idle>
              <Text size="textSm" c="gray.7">
                Drop the company logo here
              </Text>
              <Text size="textXs" c="gray.5">
                PNG, JPG or SVG - max 5MB
              </Text>
            </Flex>
          </Dropzone>
        )}
      </Stack>
    </Card>
  );
}
